import React, { useState, useEffect } from 'react';
import { Globe, Package, RefreshCw, ChevronRight, ChevronDown } from 'lucide-react';
import authService from '../services/AuthService';
import DealsViewer from '../components/DealsViewer';
import { API_BASE, SUPPLIERS } from '../consts';

const domainToSupplierName = (domain) => {
    if (!domain) return null;
    const clean = String(domain)
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .replace(/\.(com|net|org|co|us|shop|io)$/i, '');
    const match = SUPPLIERS.find(s => s.id.toLowerCase() === clean);
    return match ? match.name : clean;
};

const UniversalLiveScanPage = ({ user }) => {
    const [domains, setDomains] = useState([]);
    const [loadingDomains, setLoadingDomains] = useState(true);
    const [expandedDomains, setExpandedDomains] = useState(new Set());
    const [selectedScan, setSelectedScan] = useState(null);
    const [deals, setDeals] = useState([]);
    const [loadingDeals, setLoadingDeals] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [favoriteDeals, setFavoriteDeals] = useState([]);
    const [loadingFavorites, setLoadingFavorites] = useState(false);
    const [checkingGated, setCheckingGated] = useState(false);
    const [checkedScans, setCheckedScans] = useState(new Set());

    const toggleChecked = (scanId) => {
        setCheckedScans(prev => {
            const next = new Set(prev);
            next.has(scanId) ? next.delete(scanId) : next.add(scanId);
            return next;
        });
    };

    const sourceScans = (scans, label) => {
        if (!scans || scans.length === 0) return;
        setSelectedScan({ scan_id: null, supplier: label, created_at: new Date().toISOString() });
        loadDeals(scans.map(s => s.scan_id));
        setTimeout(() => {
            document.getElementById('deals-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const loadAllFavorites = async () => {
        setLoadingFavorites(true);
        setFavoriteDeals([]);
        try {
            const allScans = domains.flatMap(d => d.scans || []);
            const results = await Promise.all(
                allScans.map(scan =>
                    authService.authenticatedFetch(
                        API_BASE + `/api/universal_live-scan/domains/${scan.scan_id}/deals`
                    )
                        .then(r => r.json())
                        .then(data => {
                            const rows = data.deals || (Array.isArray(data) ? data : []);
                            return rows
                                .filter(row => row.favorite === true)
                                .map(row => {
                                    const mpData = row.marketplace_data || {};
                                    return {
                                        id: row.id,
                                        marketplace: row.marketplace,
                                        supplier_name: row.supplier_name || domainToSupplierName(row.supplier),
                                        product_url: row.product_url || row.url,
                                        source_price: row.cost_price ?? row.source_price,
                                        title: row.title || mpData.title,
                                        img: row.img || mpData.image,
                                        upc: row.upc || mpData.upc || '',
                                        weight: row.weight || null,
                                        gated_status: row.gated_status || null,
                                        marketplace_data: mpData,
                                        created_at: row.created_at,
                                        updated_at: row.updated_at,
                                        favorite: true,
                                        is_universal: true,
                                    };
                                });
                        })
                        .catch(() => [])
                )
            );
            setFavoriteDeals(results.flat());
        } finally {
            setLoadingFavorites(false);
        }
    };

    const toggleDomain = (supplier) => {
        setExpandedDomains(prev => {
            const next = new Set(prev);
            if (next.has(supplier)) next.delete(supplier);
            else next.add(supplier);
            return next;
        });
    };

    useEffect(() => {
        loadDomains();
    }, []);

    const loadDomains = async () => {
        setLoadingDomains(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + '/api/universal_live-scan/domains'
            );
            const data = await response.json();
            setDomains(data.domains || []);
        } catch (error) {
            console.error('Failed to load universal live scan domains:', error);
        } finally {
            setLoadingDomains(false);
        }
    };

    const loadDeals = async (scanIdOrIds) => {
        setLoadingDeals(true);
        setDeals([]);
        try {
            const ids = Array.isArray(scanIdOrIds) ? scanIdOrIds : [scanIdOrIds];
            const results = await Promise.all(ids.map(id =>
                authService.authenticatedFetch(API_BASE + `/api/universal_live-scan/domains/${id}/deals`)
                    .then(r => r.json())
                    .then(data => data.deals || (Array.isArray(data) ? data : []))
                    .catch(() => [])
            ));
            // flatten + de-dupe by row id
            const rows = [...new Map(results.flat().map(r => [r.id, r])).values()];
            const mapped = rows.map(row => {
                const mpData = row.marketplace_data || {};
                return {
                    id: row.id,
                    marketplace: row.marketplace,
                    supplier_name: row.supplier_name || domainToSupplierName(row.supplier),
                    product_url: row.product_url || row.url,
                    source_price: row.cost_price ?? row.source_price,
                    sale_price: row.sale_price,
                    profit: row.profit,
                    roi: row.roi,
                    title: row.title || mpData.title,
                    img: row.img || mpData.image,
                    upc: row.upc || mpData.upc || '',
                    weight: row.weight || null,
                    gated_status: row.gated_status || null,
                    is_new: row.is_new || false,
                    marketplace_data: mpData,
                    created_at: row.created_at,
                    updated_at: row.updated_at,
                    favorite: row.favorite ?? false,
                    is_universal: true,
                };
            });
            setDeals(mapped);
            const nullGatedDeals = mapped.filter(d => d.gated_status === null);
            checkGatedStatusBulk(nullGatedDeals);
        } catch (error) {
            console.error('Failed to load deals:', error);
        } finally {
            setLoadingDeals(false);
        }
    };

    const checkGatedStatusBulk = async (dealsToCheck) => {
        if (!dealsToCheck || dealsToCheck.length === 0) return;

        setCheckingGated(true);
        try {
            const asins = [...new Set(
                dealsToCheck
                    .map(deal => deal.marketplace_data?.asin || deal.product_id)
                    .filter(Boolean)
            )];

            if (asins.length === 0) {
                setCheckingGated(false);
                return;
            }

            const batchSize = 10;
            for (let i = 0; i < asins.length; i += batchSize) {
                const batch = asins.slice(i, i + batchSize);

                try {
                    const response = await authService.authenticatedFetch(
                        API_BASE + '/api/a2a/check-gated-bulk',
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ asins: batch })
                        }
                    );

                    const data = await response.json();

                    if (response.ok && data.results) {
                        setDeals(prevDeals =>
                            prevDeals.map(deal => {
                                const asin = deal.marketplace_data?.asin || deal.product_id;
                                const liveGatedStatus = data.results[asin];
                                if (liveGatedStatus) {
                                    return { ...deal, gated_status: liveGatedStatus };
                                }
                                return deal;
                            })
                        );
                    }
                } catch (batchError) {
                    console.error(`Gated check failed for batch ${i}, skipping:`, batchError);
                }
            }
        } catch (error) {
            console.error('Failed to check gated status:', error);
        } finally {
            setCheckingGated(false);
        }
    };

    const deleteScan = async (scanId, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this scan and all its deals?')) return;
        try {
            await authService.authenticatedFetch(
                API_BASE + `/api/universal_live-scan/domains/${scanId}`,
                { method: 'DELETE' }
            );
            setCheckedScans(prev => { const n = new Set(prev); n.delete(scanId); return n; });
            if (selectedScan?.scan_id === scanId) {
                setSelectedScan(null);
                setDeals([]);
            }
            loadDomains();
        } catch (error) {
            console.error('Failed to delete scan:', error);
        }
    };

    const handleSelectScan = (scan) => {
        setSelectedScan(scan);
        loadDeals(scan.scan_id);
        setTimeout(() => {
            document.getElementById('deals-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                            <Globe className="text-teal-600" size={40} />
                            Universal Live Scan
                        </h1>
                        <p className="text-gray-600">
                            View deals discovered across all websites.
                        </p>
                        {checkingGated && (
                            <div className="mt-4 flex items-center gap-2 text-blue-600">
                                <RefreshCw className="animate-spin" size={16} />
                                <span className="text-sm">Checking gated status for your account...</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            const next = !showFavorites;
                            setShowFavorites(next);
                            if (next) loadAllFavorites();
                        }}
                        className={`flex mt-4 items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition shadow-sm ${showFavorites
                            ? 'bg-amber-400 text-white hover:bg-amber-500'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-amber-50 hover:border-amber-400'
                            }`}
                    >
                        ⭐ {showFavorites ? 'Hide Favorites' : 'Show Favorites'}
                    </button>
                </div>

                {domains.length > 0 && (
                    <div className="flex justify-end items-center gap-3 mb-4">
                        {checkedScans.size > 0 && (
                            <button
                                onClick={() => setCheckedScans(new Set())}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Clear ({checkedScans.size})
                            </button>
                        )}
                        <button
                            onClick={() => {
                                const all = domains.flatMap(d => d.scans || []);
                                sourceScans(all.filter(s => checkedScans.has(s.scan_id)), `${checkedScans.size} Checked Scans`);
                            }}
                            disabled={checkedScans.size === 0}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Source From Checked Scans ({checkedScans.size})
                        </button>
                        <button
                            onClick={() => sourceScans(domains.flatMap(d => d.scans || []), 'All Scans')}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition"
                        >
                            Source From All Scans
                        </button>
                    </div>
                )}

                {showFavorites && (
                    <div className="mb-6">
                        <DealsViewer
                            deals={favoriteDeals}
                            loading={loadingFavorites}
                            title="⭐ Favorite Deals"
                            showFilters={true}
                            user={user}
                            pageName={'universal-live-scan'}
                        />
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    {loadingDomains ? (
                        <div className="text-center py-12">
                            <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                            <p className="text-gray-600 text-lg font-semibold">Loading scans...</p>
                        </div>
                    ) : domains.length === 0 ? (
                        <div className="text-center py-8">
                            <Package className="mx-auto text-gray-400 mb-4" size={64} />
                            <p className="text-gray-600 text-lg font-semibold mb-2">No scans found yet</p>
                            <p className="text-gray-500 text-sm mb-6">Watch the tutorial below to get started with Universal Live Scan</p>
                            <ol className="text-left max-w-lg mx-auto mb-6 space-y-2 text-sm text-gray-700">
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">1)</span> Go to any website you want to scan</li>
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">2)</span> Navigate to a catalog, results, or products page that shows prices, titles, and images</li>
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">3)</span> Right-click anywhere on the page</li>
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">4)</span> Choose <span className="font-semibold">"Run Universal Live Scan"</span></li>
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">5)</span> If needed, set the <span className="font-semibold">"Card"</span>, <span className="font-semibold">"Title"</span>, and <span className="font-semibold">"Price"</span> selectors (see video below)</li>
                                <li className="flex gap-2"><span className="font-bold text-teal-600 flex-shrink-0">6)</span> Browse the results and add items to your favorites or save all - they will appear here</li>
                            </ol>
                            <div className="max-w-2xl mx-auto rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src="https://www.youtube.com/embed/rtlDOpvow3s"
                                    title="Universal Live Scan Tutorial"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                    allowFullScreen
                                    style={{ display: 'block', width: '100%', height: '100%', minHeight: 360 }}
                                />
                            </div>
                        </div>) : (
                        <div className="space-y-3">
                            {domains.map(d => {
                                const isExpanded = expandedDomains.has(d.supplier);
                                const totalProfitPositive = (d.total_profit ?? 0) >= 0;
                                return (
                                    <div key={d.supplier} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                                        <div
                                            onClick={() => toggleDomain(d.supplier)}
                                            className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition"
                                        >
                                            <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-teal-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                                                <Globe className="text-white" size={22} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-gray-800 truncate">{d.supplier}</div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {d.scans_count} {d.scans_count === 1 ? 'scan' : 'scans'} · Latest: {d.latest_scan_at ? new Date(d.latest_scan_at).toLocaleString() : '—'}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 text-right">
                                                <span className="text-sm font-semibold text-teal-700 bg-teal-100 px-3 py-1 rounded-full">
                                                    {d.results_count} {d.results_count === 1 ? 'deal' : 'deals'}
                                                </span>
                                                <span className={`text-xs font-semibold ${totalProfitPositive ? 'text-green-700' : 'text-red-700'}`}>
                                                    Total Profit: ${Number(d.total_profit ?? 0).toFixed(2)}
                                                </span>
                                            </div>
                                            {isExpanded
                                                ? <ChevronDown className="text-gray-400" size={20} />
                                                : <ChevronRight className="text-gray-400" size={20} />}
                                        </div>

                                        {isExpanded && (
                                            <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
                                                {(d.scans || []).map(scan => {
                                                    const isSelected = selectedScan?.scan_id === scan.scan_id;
                                                    const roiPositive = (scan.avg_roi ?? 0) >= 0;
                                                    return (
                                                        <div
                                                            key={scan.scan_id}
                                                            onClick={() => handleSelectScan(scan)}
                                                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:shadow-md transition ${isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                                                                }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checkedScans.has(scan.scan_id)}
                                                                onChange={() => toggleChecked(scan.scan_id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="accent-teal-600 w-4 h-4 flex-shrink-0"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-xs text-gray-700 truncate">{scan.url}</div>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <span className="text-[11px] text-gray-400">
                                                                        {new Date(scan.created_at).toLocaleString()}
                                                                    </span>
                                                                    {scan.marketplaces?.map(mp => (
                                                                        <img
                                                                            key={mp}
                                                                            src={`/icons/${mp}.png`}
                                                                            alt={mp}
                                                                            title={mp}
                                                                            className="h-4 object-contain"
                                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-0.5 text-right">
                                                                <span className="text-xs font-semibold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full">
                                                                    {scan.results_count} {scan.results_count === 1 ? 'deal' : 'deals'}
                                                                </span>
                                                                <span className="text-[11px] text-gray-600">
                                                                    Profit: <span className="font-semibold text-gray-800">${Number(scan.total_profit ?? 0).toFixed(2)}</span>
                                                                </span>
                                                                <span className={`text-[11px] font-semibold ${roiPositive ? 'text-green-700' : 'text-red-700'}`}>
                                                                    Avg ROI: {Number(scan.avg_roi ?? 0).toFixed(2)}%
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={(e) => deleteScan(scan.scan_id, e)}
                                                                className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition"
                                                                title="Delete scan"
                                                            >
                                                                ✕
                                                            </button>
                                                            <ChevronRight className="text-gray-400" size={18} />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {selectedScan && (
                    <div id="deals-section" className="relative">
                        <button
                            onClick={() => { setSelectedScan(null); setDeals([]); }}
                            className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
                        >
                            ✕
                        </button>
                        <DealsViewer
                            deals={deals}
                            loading={loadingDeals}
                            title={`${selectedScan.supplier} - ${new Date(selectedScan.created_at).toLocaleDateString()}`}
                            showFilters={true}
                            user={user}
                            pageName={'universal-live-scan'}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default UniversalLiveScanPage;
